import React, {
  ReactElement, useMemo, useState,
} from 'react'
import {
  Spinner, Tabs, Tab, Button, Row, Col, Form,
} from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { selectUserInfo, selectAccountTz } from 'reducers/UserInfo'
import { useQueryParam, StringParam } from 'use-query-params'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateRangeInput } from 'components/Inputs/DateRangeInput'
import {
  useAllBroadcastFormRecipientResponses,
  useBroadcastFormReportGenerators,
  useBroadcastFormReportForms,
  useBroadcastFormReportGroups,
} from 'components/hooks/BroadcastFormReportHooks'

import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'
import { WarningBanner } from 'components/Common/WarningBanner'
import { downloadCSV } from 'utils/csvExport'
import { computeSentiment, getSentimentLabel } from 'utils/sentimentMapping'
import { EnterpriseSurveyReportFilter, IEnterpriseSurveyReportFilterState } from './EnterpriseSurveyReportFilter'
import { EnterpriseSurveyReportOverview } from './EnterpriseSurveyReportOverview'
import { EnterpriseSurveyReportRecipients } from './EnterpriseSurveyReportRecipients'
import { EnterpriseSurveyReportResponses } from './EnterpriseSurveyReportResponses'
import { EnterpriseSurveyReportQuestions } from './EnterpriseSurveyReportQuestions'
import { EnterpriseSurveyReportTrends } from './EnterpriseSurveyReportTrends'

type DateRangePreset = 'mostRecent' | '7' | '30' | '60' | '90' | 'custom'

// Calculate date range for a preset
const getDateRangeForPreset = (preset: DateRangePreset) => {
  if (preset === 'mostRecent') {
    return null // No date filtering
  }

  const end = moment().endOf('day')
  let start: moment.Moment

  switch (preset) {
    case '7':
      start = moment().subtract(7, 'days').startOf('day')
      break
    case '30':
      start = moment().subtract(30, 'days').startOf('day')
      break
    case '60':
      start = moment().subtract(60, 'days').startOf('day')
      break
    case '90':
      start = moment().subtract(90, 'days').startOf('day')
      break
    default:
      return null
  }

  return {
    from: start.toISOString(),
    to: end.toISOString(),
  }
}

export const NativeEnterpriseSurveyReport = (): ReactElement => {
  const userInfo = useSelector(selectUserInfo)
  const accountTz = useSelector(selectAccountTz)
  const accountId = useMemo(() => userInfo?.AccountDetail?.AccountID ?? null, [userInfo])

  const [fromUtcParam, setFromUtcParam] = useQueryParam('fromUtc', StringParam)
  const [toUtcParam, setToUtcParam] = useQueryParam('toUtc', StringParam)

  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>(() => {
    // If query params exist, check if they match a preset, otherwise default to 'mostRecent'
    if (fromUtcParam && toUtcParam) {
      const start = moment(fromUtcParam)
      const end = moment(toUtcParam)
      const daysDiff = end.diff(start, 'days')
      if (daysDiff >= 85 && daysDiff <= 95) return '90'
      if (daysDiff >= 55 && daysDiff <= 65) return '60'
      if (daysDiff >= 25 && daysDiff <= 35) return '30'
      if (daysDiff >= 5 && daysDiff <= 9) return '7'
      return 'custom'
    }
    return 'mostRecent' // Default to Most Recent (no date filtering)
  })

  const {
    data: allRecipients,
    totalRecords,
    isTruncated,
    isLoading: isLoadingRecipients,
    error: recipientsError,
    loadedCount,
    progressPercentage,
  } = useAllBroadcastFormRecipientResponses()

  const { data: forms, error: formsError } = useBroadcastFormReportForms()
  const { data: generators, error: generatorsError } = useBroadcastFormReportGenerators()
  const { data: groups, error: groupsError } = useBroadcastFormReportGroups()

  const [filter, setFilter] = React.useState<IEnterpriseSurveyReportFilterState>({
    formTypes: [],
    generatorIds: [],
    accountIds: [],
    groupIds: [],
    userTypes: [],
    serviceLevels: [],
    jobClassifications: [],
    jobRoles: [],
    employeeStatuses: [],
    employeeTypes: [],
    terminationReasons: [],
    questionIds: [],
    hasResponse: null,
    recipientNameSearch: '',
  });

  const [sortColumn, setSortColumn] = React.useState<string | null>(null)
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc')

  // Only block initial render if we have no data at all
  // Once we have some data, show it even if more is loading
  const isInitialLoad = isLoadingRecipients && allRecipients.length === 0
  const isLoadingOtherData = !forms || !generators || !groups

  const error = recipientsError
    || formsError
    || generatorsError
    || groupsError

  const filteredRecipients = useMemo(() => {
    if (!allRecipients) return []

    // Add computed sentiment to each row
    type RowWithSentiment = IBroadcastFormRecipientResponseReportRow & {
      Sentiment?: string | null
      SentimentValue?: number | null
    }
    let rows: RowWithSentiment[] = allRecipients.map(
      (row) => {
        const sentimentValue = computeSentiment(row)
        const sentimentLabel = getSentimentLabel(sentimentValue)
        return {
          ...row,
          Sentiment: sentimentLabel,
          SentimentValue: sentimentValue,
        }
      },
    )

    if (filter.formTypes.length > 0) {
      const set = new Set(filter.formTypes)
      rows = rows.filter((r) => set.has(r.FormType))
    }

    if (filter.generatorIds.length > 0) {
      const set = new Set(filter.generatorIds)
      rows = rows.filter((r) => r.GeneratorId != null && set.has(r.GeneratorId))
    }

    if (filter.accountIds.length > 0) {
      const set = new Set(filter.accountIds)
      rows = rows.filter((r) => set.has(r.AccountId))
    }

    if (filter.userTypes.length > 0) {
      const set = new Set(filter.userTypes.map((u) => u.toLowerCase()).map((u) => (u === 'resident' ? 'member' : u)))
      rows = rows.filter(
        (r) => r.UserType && set.has(r.UserType.toLowerCase()),
      )
    }

    if (filter.serviceLevels.length > 0) {
      const set = new Set(filter.serviceLevels)
      rows = rows.filter(
        (r) => r.ServiceLevel && set.has(r.ServiceLevel),
      )
    }

    if (filter.jobClassifications.length > 0) {
      const set = new Set(filter.jobClassifications)
      rows = rows.filter(
        (r) => r.JobClassification && set.has(r.JobClassification),
      )
    }

    if (filter.jobRoles.length > 0) {
      const set = new Set(filter.jobRoles)
      rows = rows.filter(
        (r) => r.JobRole && set.has(r.JobRole),
      )
    }

    if (filter.employeeStatuses.length > 0) {
      const set = new Set(filter.employeeStatuses)
      rows = rows.filter(
        (r) => r.EmployeeStatus && set.has(r.EmployeeStatus),
      )
    }

    if (filter.employeeTypes.length > 0) {
      const set = new Set(filter.employeeTypes)
      rows = rows.filter(
        (r) => r.EmployeeType && set.has(r.EmployeeType),
      )
    }

    if (filter.terminationReasons.length > 0) {
      const set = new Set(filter.terminationReasons)
      rows = rows.filter(
        (r) => r.TerminationReason && set.has(r.TerminationReason),
      )
    }

    if (filter.questionIds.length > 0) {
      const set = new Set(filter.questionIds)
      rows = rows.filter(
        (r) => r.QuestionId != null && set.has(r.QuestionId),
      )
    }

    if (filter.hasResponse === true) {
      rows = rows.filter((r) => r.HasResponse)
    } else if (filter.hasResponse === false) {
      rows = rows.filter((r) => !r.HasResponse)
    }

    if (filter.groupIds.length > 0 && groups) {
      const selectedGroups = groups.filter((g) => filter.groupIds.includes(g.GroupId))
      if (selectedGroups.length > 0) {
        const profileIdSet = new Set<number>()
        selectedGroups.forEach((g) => {
          g.ProfileIds.forEach((id) => profileIdSet.add(id))
        })

        rows = rows.filter((r) => profileIdSet.has(r.ProfileId))
      } else {
        rows = []
      }
    }

    if (filter.recipientNameSearch && filter.recipientNameSearch.trim().length > 0) {
      const searchTerm = filter.recipientNameSearch.trim().toLowerCase()
      rows = rows.filter((r) => {
        const fullName = `${r.FirstName || ''} ${r.LastName || ''}`.trim().toLowerCase()
        return fullName.includes(searchTerm)
      })
    }

    // Apply sorting
    if (sortColumn) {
      rows = [...rows].sort((a, b) => {
        let aValue: any
        let bValue: any

        switch (sortColumn) {
          case 'name':
          case 'respondentName':
            aValue = `${a.FirstName} ${a.LastName}`.toLowerCase()
            bValue = `${b.FirstName} ${b.LastName}`.toLowerCase()
            break
          case 'userType':
            aValue = (a.UserType || '').toLowerCase()
            bValue = (b.UserType || '').toLowerCase()
            break
          case 'account':
            aValue = (a.Account_Name || '').toLowerCase()
            bValue = (b.Account_Name || '').toLowerCase()
            break
          case 'formType':
            aValue = (a.FormType || '').toLowerCase()
            bValue = (b.FormType || '').toLowerCase()
            break
          case 'formName':
            aValue = (a.Name || '').toLowerCase()
            bValue = (b.Name || '').toLowerCase()
            break
          case 'responseStatus':
            aValue = a.HasResponse ? 1 : 0
            bValue = b.HasResponse ? 1 : 0
            break
          case 'responseDate':
            aValue = a.ResponseAtUtc ? new Date(a.ResponseAtUtc).getTime() : 0
            bValue = b.ResponseAtUtc ? new Date(b.ResponseAtUtc).getTime() : 0
            break
          case 'questionText':
            aValue = (a.QuestionText || '').toLowerCase()
            bValue = (b.QuestionText || '').toLowerCase()
            break
          case 'answer':
            // Sort by ResponseText first, then OptionText, then OptionValue
            aValue = (
              (a.ResponseText || '') + (a.OptionText || '') + (a.OptionValue?.toString() || '')
            ).toLowerCase()
            bValue = (
              (b.ResponseText || '') + (b.OptionText || '') + (b.OptionValue?.toString() || '')
            ).toLowerCase()
            break
          case 'scaleType':
            aValue = (a.ScaleType || '').toLowerCase()
            bValue = (b.ScaleType || '').toLowerCase()
            break
          case 'sentiment':
            aValue = ((a as any).Sentiment || '').toLowerCase()
            bValue = ((b as any).Sentiment || '').toLowerCase()
            break
          default:
            return 0
        }

        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1
        }
        return 0
      })
    }

    return rows
  }, [allRecipients, filter, groups, sortColumn, sortDirection])

  // Initialize date range on mount if not set (only if not 'mostRecent')
  const hasInitialized = React.useRef(false)
  React.useEffect(() => {
    if (!hasInitialized.current && dateRangePreset !== 'mostRecent' && (!fromUtcParam || !toUtcParam)) {
      hasInitialized.current = true
      const range = getDateRangeForPreset(dateRangePreset)
      if (range) {
        setFromUtcParam(range.from)
        setToUtcParam(range.to)
      }
    } else if (!hasInitialized.current && dateRangePreset === 'mostRecent') {
      hasInitialized.current = true
      // Clear any existing date params for 'mostRecent'
      if (fromUtcParam || toUtcParam) {
        setFromUtcParam(undefined)
        setToUtcParam(undefined)
      }
    }
  }, [dateRangePreset, fromUtcParam, toUtcParam, setFromUtcParam, setToUtcParam])

  // Date range state for DateRangeInput (MM/DD/YYYY format) - only used when custom is selected
  const getDateRangeString = useMemo(() => {
    if (fromUtcParam && toUtcParam) {
      const start = moment(fromUtcParam).format('MM/DD/YYYY')
      const end = moment(toUtcParam).format('MM/DD/YYYY')
      return `${start} - ${end}`
    }
    return ''
  }, [fromUtcParam, toUtcParam])

  const [dateRangeStr, setDateRangeStr] = useState(getDateRangeString)

  // Update date range string when query params change (only if custom is selected)
  React.useEffect(() => {
    if (dateRangePreset === 'custom') {
      setDateRangeStr(getDateRangeString)
    }
  }, [getDateRangeString, dateRangePreset])

  const handlePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset)
    if (preset === 'mostRecent') {
      // Clear date params for 'mostRecent'
      setFromUtcParam(undefined)
      setToUtcParam(undefined)
    } else if (preset !== 'custom') {
      const range = getDateRangeForPreset(preset)
      if (range) {
        setFromUtcParam(range.from)
        setToUtcParam(range.to)
      }
    }
  }

  const handleDateRangeChange = (dateStr: string) => {
    setDateRangeStr(dateStr)
    const [startDate, endDate] = dateStr.split(' - ')
    if (startDate && endDate) {
      const startMoment = moment(startDate, 'MM/DD/YYYY', true)
      const endMoment = moment(endDate, 'MM/DD/YYYY', true)
      if (startMoment.isValid() && endMoment.isValid()) {
        setFromUtcParam(startMoment.startOf('day').toISOString())
        setToUtcParam(endMoment.endOf('day').toISOString())
      }
    } else {
      setFromUtcParam(undefined)
      setToUtcParam(undefined)
    }
  }

  const handleExportRawData = () => {
    if (!filteredRecipients || filteredRecipients.length === 0) {
      return
    }

    // Define headers for all available fields
    const headers = [
      'Form ID',
      'Account ID',
      'Account Name',
      'Form Type',
      'Form Name',
      'Is Complete',
      'Enterprise Account ID',
      'Generator ID',
      'Form Session ID',
      'Profile ID',
      'Has Response',
      'Response At (UTC)',
      'Expiry Time (UTC)',
      'Created At (UTC)',
      'Is Anonymous',
      'First Name',
      'Last Name',
      'User Type',
      'Is Deleted',
      'Job Classification',
      'Job Role',
      'Start Date Processed',
      'End Date Processed',
      'Employee Status',
      'Employee Type',
      'Termination Reason',
      'Service Level',
      'Response ID',
      'Question ID',
      'Response Option ID',
      'Response Text',
      'Option Value',
      'Option Text',
      'Scale Type',
      'Question Category',
      'Question Text',
      'Sentiment',
    ]

    const rows = filteredRecipients.map((row) => [
      row.FormId || '',
      row.AccountId || '',
      row.Account_Name || '',
      row.FormType || '',
      row.Name || '',
      row.IsComplete ? 'Yes' : 'No',
      row.EnterpriseAccountId || '',
      row.GeneratorId || '',
      row.FormSessionId || '',
      row.ProfileId || '',
      row.HasResponse ? 'Yes' : 'No',
      row.ResponseAtUtc || '',
      row.ExpiryTimeUtc || '',
      row.CreatedAtUtc || '',
      row.IsAnonymous ? 'Yes' : 'No',
      row.FirstName || '',
      row.LastName || '',
      row.UserType || '',
      row.IsDeleted ? 'Yes' : 'No',
      row.JobClassification || '',
      row.JobRole || '',
      row.StartDateProcessed || '',
      row.EndDateProcessed || '',
      row.EmployeeStatus || '',
      row.EmployeeType || '',
      row.TerminationReason || '',
      row.ServiceLevel || '',
      row.ResponseId || '',
      row.QuestionId || '',
      row.ResponseOptionId || '',
      row.ResponseText || '',
      row.OptionValue || '',
      row.OptionText || '',
      row.ScaleType || '',
      row.QuestionCategory || '',
      row.QuestionText || '',
      (row as any).Sentiment || '',
    ])

    downloadCSV(headers, rows, 'survey_report_raw_data')
  }

  if (!accountId || (isInitialLoad && isLoadingOtherData)) {
    return (
      <div className="loading-text d-flex align-items-center">
        <Spinner animation="border" variant="primary" role="status" className="mr-3" />
        Loading...
      </div>
    )
  }

  if (error) {
    return (
      <div className="loading-text d-flex align-items-center">
        {error?.response?.data?.Message || 'An error has occurred'}
      </div>
    )
  }

  if (!allRecipients && !isLoadingRecipients) {
    return <h2>Unable to get report data.</h2>
  }

  // If we have no data yet and are still loading, show loading state
  if (!allRecipients || allRecipients.length === 0) {
    if (isInitialLoad) {
      return (
        <div className="loading-text d-flex align-items-center">
          <Spinner animation="border" variant="primary" role="status" className="mr-3" />
          Loading...
        </div>
      )
    }
  }

  return (
    <div>
      {/* Header Section */}
      <div className="mb-4">
        <Row className="align-items-center">
          <Col md={10}>
            <h2 className="mb-0">Survey Report</h2>
          </Col>
          <Col md={2} className="text-right">
            <Button
              variant="primary"
              onClick={handleExportRawData}
              disabled={!filteredRecipients || filteredRecipients.length === 0}
            >
              <FontAwesomeIcon icon="download" className="mr-2" />
              Export Raw Data
            </Button>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Response Date Range</Form.Label>
              <Form.Control
                as="select"
                value={dateRangePreset}
                onChange={(e) => handlePresetChange(e.target.value as DateRangePreset)}
              >
                <option value="mostRecent">Most Recent</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="60">Last 60 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="custom">Custom</option>
              </Form.Control>
              {dateRangePreset === 'custom' && (
                <div className="mt-2">
                  <DateRangeInput
                    autoComplete="off"
                    value={dateRangeStr}
                    onChange={handleDateRangeChange}
                    isOutsideRange={(day) => day.isAfter(moment())}
                    label=""
                    format="MM/DD/YYYY"
                    mask="99/99/9999 - 99/99/9999"
                    navigationUnit="day"
                    id="reportDateRange"
                    name="reportDateRange"
                    className="mb-0"
                    accountTimezone={accountTz}
                  />
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      </div>

      {isTruncated && (
        <WarningBanner
          message={
            'The maximum number of rows (100,000) has been loaded. '
            + 'Your date range includes more responses than this. '
            + 'Please use a smaller time window to see all results.'
          }
        />
      )}

      {isLoadingRecipients && totalRecords > 0 && (
        <div className="alert alert-info d-flex align-items-center mb-3">
          <Spinner animation="border" variant="info" size="sm" role="status" className="mr-3" />
          <div className="flex-grow-1">
            <div className="mb-2">
              <strong>
                {loadedCount.toLocaleString()}
                {' '}
                /
                {' '}
                {totalRecords.toLocaleString()}
                {' '}
                question responses loaded
                {' '}
                (
                {progressPercentage}
                %)
              </strong>
            </div>
            <div className="progress" style={{ height: '8px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${progressPercentage}%` }}
                aria-valuenow={progressPercentage}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <span className="sr-only">
                  {progressPercentage}
                  % complete
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <EnterpriseSurveyReportFilter
        filter={filter}
        onChange={setFilter}
        forms={forms}
        generators={generators}
        groups={groups || []}
        allRecipients={allRecipients}
        filteredCount={filteredRecipients.length}
      />

      <Tabs defaultActiveKey="overview" id="enterprise-survey-report-tabs" className="mt-4">
        <Tab eventKey="overview" title="Overview">
          <EnterpriseSurveyReportOverview filteredRecipients={filteredRecipients} />
        </Tab>
        <Tab eventKey="recipients" title="Recipients">
          <EnterpriseSurveyReportRecipients
            filteredRecipients={filteredRecipients}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={(column, direction) => {
              setSortColumn(column)
              setSortDirection(direction)
            }}
          />
        </Tab>
        <Tab eventKey="responses" title="Responses">
          <EnterpriseSurveyReportResponses
            filteredRecipients={filteredRecipients}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={(column, direction) => {
              setSortColumn(column)
              setSortDirection(direction)
            }}
          />
        </Tab>
        <Tab eventKey="questions" title="Questions">
          <EnterpriseSurveyReportQuestions filteredRecipients={filteredRecipients} />
        </Tab>
        <Tab eventKey="trends" title="Trends">
          <EnterpriseSurveyReportTrends filteredRecipients={filteredRecipients} />
        </Tab>
      </Tabs>
    </div>
  )
}
